export type CalidadObjetivoSeguimiento = {
  id: number;
  objetivo_id: number;
  seguimiento: string | null;
  firma: boolean;
  firma_usuario_id: number | null;
  firma_fecha: string | null;
  cierre_objetivo: string | null;
  usuario_modificacion_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CalidadObjetivoEtapa = {
  id: number;
  objetivo_id: number;
  orden: number;
  descripcion: string;
  responsable_ejecucion: string | null;
  coste: number;
  esfuerzo: string | null;
  responsable_seguimiento: string | null;
  plazo: string | null;
  usuario_modificacion_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CalidadObjetivoAnexo = {
  id: number;
  objetivo_id: number;
  archivo_nombre: string;
  archivo_mime: string | null;
  archivo_size: number | null;
  usuario_modificacion_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CalidadObjetivo = {
  id: number;
  anio: number;
  numero_objetivo: string;
  definicion: string;
  cerrado: boolean;
  observaciones: string | null;
  usuario_modificacion_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  etapas: CalidadObjetivoEtapa[];
  seguimiento: CalidadObjetivoSeguimiento | null;
  anexos: CalidadObjetivoAnexo[];
};

export type CalidadObjetivoFilters = {
  anio: string;
  cerrado: "" | "si" | "no";
  search: string;
};

export type CalidadObjetivoMeta = {
  total: number;
  years: number[];
};

export type CalidadObjetivoPayload = {
  anio: number;
  numero_objetivo: string;
  definicion: string;
  cerrado: boolean;
  observaciones: string | null;
  seguimiento?: {
    seguimiento: string | null;
    firma: boolean;
    cierre_objetivo: string | null;
  };
};

export type CalidadObjetivoFormValues = {
  anio: string;
  numero_objetivo: string;
  definicion: string;
  cerrado: boolean;
  observaciones: string;
  seguimiento: string;
  firma: boolean;
  cierre_objetivo: string;
};

export type CalidadObjetivoEtapaFormValues = {
  orden: string;
  descripcion: string;
  responsable_ejecucion: string;
  coste: string;
  esfuerzo: string;
  responsable_seguimiento: string;
  plazo: string;
};

export type CalidadObjetivoCloneSource = {
  source_id: number;
  source_anio: number;
  anio: number;
  numero_objetivo: string;
  definicion: string;
  observaciones: string | null;
  seguimiento: string | null;
  cierre_objetivo: string | null;
};

export const defaultObjetivoFilters: CalidadObjetivoFilters = {
  anio: "",
  cerrado: "",
  search: "",
};

export function createEmptyObjetivoForm(anio = new Date().getFullYear()): CalidadObjetivoFormValues {
  return {
    anio: String(anio),
    numero_objetivo: "",
    definicion: "",
    cerrado: false,
    observaciones: "",
    seguimiento: "",
    firma: false,
    cierre_objetivo: "",
  };
}

export function mapObjetivoToForm(objetivo: CalidadObjetivo): CalidadObjetivoFormValues {
  return {
    anio: String(objetivo.anio),
    numero_objetivo: objetivo.numero_objetivo,
    definicion: objetivo.definicion,
    cerrado: objetivo.cerrado,
    observaciones: objetivo.observaciones ?? "",
    seguimiento: objetivo.seguimiento?.seguimiento ?? "",
    firma: objetivo.seguimiento?.firma ?? false,
    cierre_objetivo: objetivo.seguimiento?.cierre_objetivo ?? "",
  };
}

export function mapCloneSourceToForm(source: CalidadObjetivoCloneSource): CalidadObjetivoFormValues {
  return {
    anio: String(source.anio),
    numero_objetivo: source.numero_objetivo,
    definicion: source.definicion,
    cerrado: false,
    observaciones: source.observaciones ?? "",
    seguimiento: source.seguimiento ?? "",
    firma: false,
    cierre_objetivo: source.cierre_objetivo ?? "",
  };
}

export function mapObjetivoFormToPayload(values: CalidadObjetivoFormValues): CalidadObjetivoPayload {
  return {
    anio: Number.parseInt(values.anio, 10),
    numero_objetivo: values.numero_objetivo.trim(),
    definicion: values.definicion.trim(),
    cerrado: values.cerrado,
    observaciones: normalizeText(values.observaciones),
    seguimiento: {
      seguimiento: normalizeText(values.seguimiento),
      firma: values.firma,
      cierre_objetivo: normalizeText(values.cierre_objetivo),
    },
  };
}

export function createEmptyEtapaForm(nextOrden = 1): CalidadObjetivoEtapaFormValues {
  return {
    orden: String(nextOrden),
    descripcion: "",
    responsable_ejecucion: "",
    coste: "0",
    esfuerzo: "",
    responsable_seguimiento: "",
    plazo: "",
  };
}

export function mapEtapaToForm(etapa: CalidadObjetivoEtapa): CalidadObjetivoEtapaFormValues {
  return {
    orden: String(etapa.orden),
    descripcion: etapa.descripcion,
    responsable_ejecucion: etapa.responsable_ejecucion ?? "",
    coste: String(etapa.coste ?? 0),
    esfuerzo: etapa.esfuerzo ?? "",
    responsable_seguimiento: etapa.responsable_seguimiento ?? "",
    plazo: etapa.plazo ?? "",
  };
}

export function mapEtapaFormToPayload(values: CalidadObjetivoEtapaFormValues) {
  return {
    orden: Number.parseInt(values.orden, 10) || 1,
    descripcion: values.descripcion.trim(),
    responsable_ejecucion: normalizeText(values.responsable_ejecucion),
    coste: Number.parseFloat(values.coste.replace(",", ".")) || 0,
    esfuerzo: normalizeText(values.esfuerzo),
    responsable_seguimiento: normalizeText(values.responsable_seguimiento),
    plazo: normalizeText(values.plazo),
  };
}

function normalizeText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
